import { Fragment } from "react";
import { ChevronRight, DoorOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const COLLAPSED_SEGMENT = "…";

interface PhotoRoomLabelsProps {
  /** Space/room names, ancestors first. */
  roomPath: string[] | undefined;
  /** Tiles fold a deep path to "Building A › … › Bathroom" so the room itself never gets cut off. */
  collapse?: boolean;
  className?: string;
}

function visibleSegments(roomPath: string[], collapse: boolean): string[] {
  if (!collapse || roomPath.length <= 2) return roomPath;
  return [roomPath[0], COLLAPSED_SEGMENT, roomPath[roomPath.length - 1]];
}

/**
 * The space a photo sits in, as one path pill. Derived from where the photo lives rather than stored on
 * it, so it follows a rename or a move — and read-only, which is why it's shaped differently from the
 * labels a user types.
 *
 * No flag check here: the server omits `roomPath` for orgs without `PHOTO_LABELS_ENABLED`.
 */
export function PhotoRoomLabels({ roomPath = [], collapse = false, className }: PhotoRoomLabelsProps) {
  if (roomPath.length === 0) return null;

  const segments = visibleSegments(roomPath, collapse);
  const fullPath = roomPath.join(" / ");

  return (
    <div className={className}>
      <Badge
        variant="outline"
        title={fullPath}
        className="max-w-full min-w-0 gap-1 bg-transparent font-medium text-muted-foreground"
      >
        <span className="sr-only">Room: {fullPath}</span>
        <DoorOpen className="h-3 w-3 shrink-0" aria-hidden="true" />
        {segments.map((segment, index) => {
          const isLeaf = index === segments.length - 1;
          return (
            <Fragment key={`${segment}-${index}`}>
              {index > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/60" aria-hidden="true" />}
              <span aria-hidden="true" className={cn("min-w-0 truncate", isLeaf && "max-w-full shrink-0")}>
                {segment}
              </span>
            </Fragment>
          );
        })}
      </Badge>
    </div>
  );
}
